import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { Download } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useAuditLog, useUsers } from "@/store/hooks";
import { useDemoStore } from "@/store/useDemoStore";
import { downloadCSV, downloadJSON } from "@/utils/download";

export default function AuditorBitacora() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("auditor"), [setRole]);

  const auditLog = useAuditLog();
  const users = useUsers();

  const [fRol, setFRol] = useState<string>("");
  const [fEntidad, setFEntidad] = useState<string>("");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return auditLog.filter((ev) => {
      if (fRol && ev.rol !== fRol) return false;
      if (fEntidad && ev.entidad !== fEntidad) return false;
      if (q) {
        const u = users[ev.actor];
        const hay = `${ev.accion} ${ev.entidadId} ${ev.actor} ${u?.nombre ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [auditLog, fRol, fEntidad, query, users]);

  const handleExport = (kind: "csv" | "json") => {
    const rows = filtered.map((ev) => ({
      ts: ev.ts,
      actor_id: ev.actor,
      actor_nombre: users[ev.actor]?.nombre ?? "",
      rol: ev.rol,
      accion: ev.accion,
      entidad: ev.entidad,
      entidad_id: ev.entidadId,
    }));
    const fname = `bitacora-${new Date().toISOString().slice(0, 10)}`;
    if (kind === "csv") downloadCSV(`${fname}.csv`, rows);
    else downloadJSON(`${fname}.json`, rows);
  };

  const roles = Array.from(new Set(auditLog.map((e) => e.rol)));
  const entidades = Array.from(new Set(auditLog.map((e) => e.entidad)));

  return (
    <AppLayout pageTitle="Bitácora del sistema">
      <div style={{ marginBottom: 14 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>Bitácora de cambios</h1>
        <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>Eventos registrados por la plataforma con actor, rol y entidad afectada.</div>
      </div>

      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr auto auto", gap: 10, alignItems: "end" }}>
          <Field label="Rol">
            <select data-testid="filter-rol" value={fRol} onChange={(e) => setFRol(e.target.value)} style={selStyle}>
              <option value="">Todos</option>
              {roles.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Entidad">
            <select data-testid="filter-entidad" value={fEntidad} onChange={(e) => setFEntidad(e.target.value)} style={selStyle}>
              <option value="">Todas</option>
              {entidades.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Buscar">
            <input data-testid="input-search" type="text" placeholder="actor, acción o ID..." value={query} onChange={(e) => setQuery(e.target.value)} style={selStyle} />
          </Field>
          <PrimaryButton variant="outline" testId="button-export-csv" onClick={() => handleExport("csv")}><Download size={13} /> CSV</PrimaryButton>
          <PrimaryButton variant="outline" testId="button-export-json" onClick={() => handleExport("json")}><Download size={13} /> JSON</PrimaryButton>
        </div>
      </Card>

      <Card padding={0}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--cc-gray-200)" }}>
          <SectionTitle action={<span style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{filtered.length} eventos</span>}>
            Eventos
          </SectionTitle>
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--cc-gray-500)", fontSize: 13 }} data-testid="text-no-events">
            Sin eventos para los filtros activos.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }} data-testid="table-bitacora">
            <thead>
              <tr style={{ background: "var(--cc-gray-50)", textAlign: "left" }}>
                {["Fecha y hora", "Actor", "Rol", "Acción", "Entidad"].map((h) => (
                  <th key={h} style={{ padding: "10px 14px", fontSize: 10, fontWeight: 600, color: "var(--cc-gray-500)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((ev) => {
                const u = users[ev.actor];
                const isExp = ev.entidad === "expediente";
                return (
                  <tr key={ev.id} style={{ borderTop: "1px solid var(--cc-gray-100)" }}>
                    <td style={{ padding: "10px 14px", color: "var(--cc-gray-600)", fontFamily: "var(--app-font-mono)" }}>{ev.ts.slice(0, 19).replace("T", " ")}</td>
                    <td style={{ padding: "10px 14px", color: "var(--cc-navy)", fontWeight: 600 }}>{u?.nombre ?? ev.actor}</td>
                    <td style={{ padding: "10px 14px" }}><Badge tone="gray">{ev.rol}</Badge></td>
                    <td style={{ padding: "10px 14px", color: "var(--cc-gray-700)" }}>{ev.accion}</td>
                    <td style={{ padding: "10px 14px", color: "var(--cc-teal)" }}>
                      {isExp ? (
                        <Link href={`/auditor/expediente/${ev.entidadId}`} data-testid={`link-${ev.id}`} style={{ color: "var(--cc-teal)", textDecoration: "none" }}>
                          {ev.entidad}: {ev.entidadId}
                        </Link>
                      ) : (
                        <span>{ev.entidad}: {ev.entidadId}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </AppLayout>
  );
}

const selStyle: React.CSSProperties = { padding: "7px 8px", border: "1px solid var(--cc-gray-200)", borderRadius: 8, fontSize: 12, background: "white", color: "var(--cc-navy)", width: "100%" };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--cc-gray-500)", letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}
